import React, { createContext, useState, ReactNode, useContext } from 'react';
import SnackbarAlert from '../components/CommonComponents/SnackbarAlert';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
  token: string | null;
  roles: string[];
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, newPassword?: string) => Promise<void>;
  logout: () => void;
}

// Extend the JwtPayload type to include custom attributes
interface CustomJwtPayload {
  'custom:roles'?: string;
}

// Function to decode the token and extract roles
const decodeToken = (token: string): CustomJwtPayload => {
  return jwtDecode<CustomJwtPayload>(token);
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('guidedPathwaysIdToken'));
  const [roles, setRoles] = useState<string[]>(() => {
    const storedRoles = localStorage.getItem('guidedPathwaysIdTokenRoles');
    return storedRoles ? JSON.parse(storedRoles) : [];
  });
  const [loading, setLoading] = useState<boolean>(false);
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;

  const handleClick = (msg: string, sev: string) => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  };

  const handleClose = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const login = async (email: string, password: string, newPassword?: string) => {
    setLoading(true);
    setError(null); // Reset error state before attempting login
    const authEndpoint = baseApiUrl + "/auth";
    const payload = {
      email,
      password,
      newPassword
    };
    try {
      const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.idToken) {
        const decodedToken = decodeToken(data.idToken);
        const rolesString = decodedToken['custom:roles'] || '';
        const rolesArray = rolesString.split(',').map(role => role.trim());
        // for testing
        console.log('roles', rolesArray)
        localStorage.setItem('guidedPathwaysIdToken', data.idToken);
        localStorage.setItem('guidedPathwaysIdTokenRoles', JSON.stringify(rolesArray));
        setRoles(rolesArray);
        setToken(data.idToken);
        handleClick("Successfully logged in", "success");
      } 
      else if (data.error){
        // this means the error likely came from the authentication endpoint
        const errorMessage = data.error.message || "Unknown error occurred";
        setError(data.errorCode);
        handleClick(errorMessage, "error");
      } else if (data.message) {
        // this means there was likely an error and we handled it with our own respons
        setError(data.errorCode);
        if(data.errorCode === "NEW_PASSWORD_REQUIRED"){
          handleClick(data.message, "info"); // this is just informational that they need to set a new password
        } else {
          handleClick(data.message, "error");
        }
      } else {
        // this should be unexpected as we should have likely handled the error by here
        const unexpectedError = data.errorCode || "Unknown error occurred";
        setError(unexpectedError);
        handleClick(unexpectedError, "error");
        console.log(`Unexpected error: ${data}`);
      }
    } catch (err) {
      const networkError = "Network error occurred";
      setError(networkError);
      handleClick(networkError, "error");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('guidedPathwaysIdToken');
    localStorage.removeItem('guidedPathwaysIdTokenRoles');
    setToken(null);
    setRoles([]);
  };

  return (
    <div>
      <SnackbarAlert
        open={open}
        handleClose={handleClose}
        severity={severity}
        message={message}
      />
      <AuthContext.Provider value={{ token, roles, loading, error, login, logout }}>
        {children}
      </AuthContext.Provider>
    </div>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
